import { useEffect, useState } from "react";
import { Sparkles, X, AlertCircle, Quote, Settings, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PRIORITY_LABELS, PRIORITY_COLORS } from "@/lib/taskHelpers";

/**
 * AI Suggest Tasks Dialog — review-first AI workflow.
 *
 * - On open: GETs /workspaces/{id}/ai-status to decide whether to call the AI
 * - If unavailable: shows the SPECIFIC reason (server key missing / workspace disabled)
 *   with a link to Settings, and disables the action.
 * - If available: POSTs to /tasks/ai-suggest, renders suggestion cards.
 * - User can: select/deselect, inline-edit (title/description/priority/assignee/due),
 *   expand source excerpt, then submit selected items to /tasks/from-suggestions.
 * - Nothing is persisted until the user clicks "Create N tasks".
 */
export default function AISuggestTasksDialog({ open, onOpenChange, meetingId, members = [], onCreated }) {
  const { currentWorkspace } = useWorkspace();
  const [phase, setPhase] = useState("idle"); // idle | checking | unavailable | loading | ready | submitting
  const [aiStatus, setAiStatus] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open || !currentWorkspace) return;
    setPhase("checking");
    setSuggestions([]);
    setSelectedIds(new Set());
    setMeta(null);
    api
      .get(`/workspaces/${currentWorkspace.workspace_id}/ai-status`)
      .then((r) => {
        setAiStatus(r.data);
        if (!r.data.available) {
          setPhase("unavailable");
        } else {
          fetchSuggestions();
        }
      })
      .catch(() => {
        setAiStatus({ available: false, reasons: ["network_error"] });
        setPhase("unavailable");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentWorkspace, meetingId]);

  const fetchSuggestions = async () => {
    setPhase("loading");
    try {
      const { data } = await api.post(`/tasks/ai-suggest`, { meeting_id: meetingId });
      const enriched = (data.suggestions || []).map((s, i) => ({ ...s, _id: `s${i}` }));
      setSuggestions(enriched);
      setSelectedIds(new Set(enriched.map((s) => s._id)));
      setMeta({ generated_at: data.generated_at, model: data.model, meeting_title: data.meeting_title });
      setPhase("ready");
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not generate suggestions";
      toast.error(detail);
      setAiStatus({ available: false, reasons: ["error"], message: detail });
      setPhase("unavailable");
    }
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const updateField = (id, field, value) =>
    setSuggestions((prev) => prev.map((s) => (s._id === id ? { ...s, [field]: value } : s)));

  const handleCreate = async () => {
    const items = suggestions
      .filter((s) => selectedIds.has(s._id))
      .map((s) => ({
        title: s.title,
        description: s.description,
        priority: s.priority,
        assignee_id: s.suggested_assignee_id,
        due_date: s.due_date,
        source_excerpt: s.source_excerpt,
      }));
    if (items.length === 0) {
      toast.error("Select at least one task to create");
      return;
    }
    setPhase("submitting");
    try {
      const { data } = await api.post(`/tasks/from-suggestions`, {
        meeting_id: meetingId,
        items,
      });
      toast.success(`Created ${data.created} task${data.created === 1 ? "" : "s"} from AI suggestions`);
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create tasks");
      setPhase("ready");
    }
  };

  const reasonCopy = (r) => {
    if (r === "server_key_missing")
      return {
        title: "AI is not configured on this server",
        body: "An admin needs to add EMERGENT_LLM_KEY to backend/.env and restart Bloq to enable AI task suggestions.",
      };
    if (r === "workspace_disabled")
      return {
        title: "AI features are turned off for this workspace",
        body: "An owner or admin can enable them in Settings → Workspace.",
      };
    return {
      title: "Could not reach the AI service",
      body: aiStatus?.message || "Please try again in a moment, or contact support if it keeps failing.",
    };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-suggest-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}>
              <Sparkles className="w-4 h-4" />
            </span>
            <DialogTitle>Suggest tasks from meeting notes</DialogTitle>
          </div>
        </DialogHeader>

        <p className="text-xs text-[#82828c] mt-1 -mb-1">
          AI suggestions should be reviewed before assigning. Nothing is saved until you click create.
        </p>

        {phase === "checking" && <CenterSpinner text="Checking AI availability…" />}
        {phase === "loading" && <CenterSpinner text="Reading the meeting context…" />}

        {phase === "unavailable" && (
          <div className="bg-[#f5efe6] rounded-lg p-5 flex flex-col gap-3 mt-3" data-testid="ai-unavailable">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 mt-0.5 text-[#b45309] flex-shrink-0" />
              <div className="flex-1">
                {(aiStatus?.reasons || []).map((r) => {
                  const c = reasonCopy(r);
                  return (
                    <div key={r} className="mb-2 last:mb-0">
                      <p className="font-medium text-sm">{c.title}</p>
                      <p className="text-xs text-[#4a4a52] mt-0.5">{c.body}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            {aiStatus?.reasons?.includes("workspace_disabled") && (
              <Link
                to="/app/settings?tab=workspace"
                onClick={() => onOpenChange(false)}
                className="bloq-btn-secondary inline-flex items-center gap-2 text-xs self-start"
                data-testid="ai-go-to-settings"
              >
                <Settings className="w-3.5 h-3.5" /> Open workspace settings
              </Link>
            )}
          </div>
        )}

        {phase === "ready" && (
          <>
            <p className="text-xs text-[#82828c] mt-2">
              {meta?.model && `${meta.model} · `}
              {meta?.generated_at && `Generated ${new Date(meta.generated_at).toLocaleString()}`}
            </p>

            {suggestions.length === 0 ? (
              <div className="bg-[#f5efe6] rounded-lg p-6 text-center mt-3">
                <p className="font-medium">No actionable items found</p>
                <p className="text-xs text-[#82828c] mt-1">
                  Add more notes or generate an AI summary first, then try again.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col gap-3 mt-3">
                {suggestions.map((s) => (
                  <SuggestionCard
                    key={s._id}
                    s={s}
                    selected={selectedIds.has(s._id)}
                    expanded={expanded.has(s._id)}
                    members={members}
                    onToggleSelect={() => toggleSelect(s._id)}
                    onToggleExpand={() => toggleExpand(s._id)}
                    onUpdate={(field, value) => updateField(s._id, field, value)}
                  />
                ))}
              </ul>
            )}

            <div className="mt-5 flex items-center justify-between flex-wrap gap-3 border-t pt-4">
              <p className="text-xs text-[#82828c]">
                {selectedIds.size} of {suggestions.length} selected
              </p>
              <div className="flex gap-2">
                <button onClick={() => onOpenChange(false)} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid="ai-cancel">
                  Cancel
                </button>
                <button
                  onClick={fetchSuggestions}
                  disabled={phase === "submitting"}
                  className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                  data-testid="ai-regenerate"
                >
                  <Sparkles className="w-3 h-3" /> Re-generate
                </button>
                <button
                  onClick={handleCreate}
                  disabled={selectedIds.size === 0 || phase === "submitting"}
                  className="bloq-btn-primary text-xs px-4 py-1.5 inline-flex items-center gap-1.5"
                  data-testid="ai-create-selected"
                >
                  {phase === "submitting" ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" /> Creating…
                    </>
                  ) : (
                    <>Create {selectedIds.size} {selectedIds.size === 1 ? "task" : "tasks"}</>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CenterSpinner({ text }) {
  return (
    <div className="py-12 flex flex-col items-center gap-3">
      <Loader2 className="w-7 h-7 animate-spin text-[#005e7a]" />
      <p className="text-sm text-[#4a4a52]">{text}</p>
    </div>
  );
}

function SuggestionCard({ s, selected, expanded, members, onToggleSelect, onToggleExpand, onUpdate }) {
  const prio = PRIORITY_COLORS[s.priority] || PRIORITY_COLORS.medium;
  const confidenceLabel = s.confidence >= 0.85 ? "High" : s.confidence >= 0.6 ? "Medium" : "Low";
  return (
    <li
      className={`bloq-card p-4 transition-all ${selected ? "" : "opacity-60"}`}
      style={selected ? { outline: "2px solid rgba(0,94,122,0.3)" } : {}}
      data-testid={`ai-suggestion-${s._id}`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="mt-1.5 accent-[#005e7a] w-4 h-4 flex-shrink-0"
          data-testid={`ai-suggestion-checkbox-${s._id}`}
        />
        <div className="flex-1 min-w-0 space-y-2.5">
          <input
            value={s.title}
            onChange={(e) => onUpdate("title", e.target.value)}
            className="w-full font-medium text-sm bg-transparent border-none outline-none focus:bg-[#f5efe6] rounded px-1.5 py-1 -ml-1.5"
            data-testid={`ai-suggestion-title-${s._id}`}
          />
          <textarea
            value={s.description}
            onChange={(e) => onUpdate("description", e.target.value)}
            placeholder="Description"
            rows={2}
            className="w-full text-xs text-[#4a4a52] bg-transparent border-none outline-none focus:bg-[#f5efe6] rounded px-1.5 py-1 -ml-1.5 resize-none"
            data-testid={`ai-suggestion-desc-${s._id}`}
          />
          <div className="flex items-center flex-wrap gap-2">
            <Select value={s.priority} onValueChange={(v) => onUpdate("priority", v)}>
              <SelectTrigger className="h-7 w-28 text-xs" data-testid={`ai-suggestion-priority-${s._id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={s.suggested_assignee_id || "none"}
              onValueChange={(v) => onUpdate("suggested_assignee_id", v === "none" ? null : v)}
            >
              <SelectTrigger className="h-7 w-44 text-xs" data-testid={`ai-suggestion-assignee-${s._id}`}>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="date"
              value={s.due_date ? s.due_date.slice(0, 10) : ""}
              onChange={(e) => onUpdate("due_date", e.target.value ? new Date(e.target.value).toISOString() : null)}
              className="h-7 text-xs px-2 rounded bg-white shadow-sm border-none outline-none"
              data-testid={`ai-suggestion-due-${s._id}`}
            />
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: prio.bg, color: prio.c }}
            >
              {PRIORITY_LABELS[s.priority]}
            </span>
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: "rgba(0,94,122,0.08)", color: "#005e7a" }}
              title={`AI confidence ${(s.confidence * 100).toFixed(0)}%`}
            >
              {confidenceLabel} confidence
            </span>
          </div>
          {s.source_excerpt && (
            <div>
              <button
                onClick={onToggleExpand}
                className="text-[11px] inline-flex items-center gap-1 text-[#005e7a] hover:underline"
                data-testid={`ai-suggestion-source-toggle-${s._id}`}
              >
                <Quote className="w-3 h-3" /> {expanded ? "Hide source" : "Show source from meeting"}
              </button>
              {expanded && (
                <blockquote
                  className="mt-1.5 text-xs text-[#4a4a52] border-l-2 pl-2.5 italic"
                  style={{ borderColor: "#005e7a" }}
                >
                  {s.source_excerpt}
                </blockquote>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
